/**
 * Hunter kit identity (plan 29 F9, lane BY 2026-10-11).
 *
 * Authored looks for the reachable hunter abilities that resolved to a school
 * default or shared a look with a kit-mate: the ranger auto, Aimed Shot and
 * Multiple Arrows all landed as the same red hit_physical puff; Aimed Shot's
 * half-second wind-up was a faint generic glow; Disengage borrowed
 * `ranged_projectile`; Concussive Shot, Kill Shot and Deterrence named defs
 * that did not exist; Explosive Shot's blast was ~135 px for an 80 px splash.
 * Each def is named by the ability's TOML (`cast_effect` / `impact_effect`)
 * or its module (muzzle flash, wind-up, splash), so every screen reads the
 * same id.
 *
 * Colours follow each ability's TOML `aura_color`: the auto's tan wood, Aimed
 * Shot red, Multiple Arrows fletching green, Disengage slate, Rain of Arrows
 * brown, Concussive Shot amber, Explosive Shot flame orange, Kill Shot gold,
 * Deterrence sky blue. Bursts end; no screen shake or flash (plan 29 F8).
 */
import type { VFXEffectConfig } from '../../VFXEffectRegistry';
import { ringBlast, shardBurst, glintScatter, groundCloud, wispRise, starFlash, stagedBlast } from './burstBuilders';

const WOOD = 0xd4a574;
const WOOD_DARK = 0x8b5a2b;
const RED = 0xff3030;
const RED_HOT = 0xffc0b0;
const FLETCH = 0x8fbf3f;
const SLATE = 0x6a8caf;
const BROWN = 0x8b4513;
const AMBER = 0xffaa00;
const AMBER_HOT = 0xffe7a0;
const FLAME = 0xff5a00;
const FLAME_HOT = 0xffd080;
const GOLD = 0xffe066;
const SKY = 0x88ccff;
const SKY_CORE = 0xe8f6ff;

/** The ranger auto's hit: the arrow thunks in — prominent splinter spray, impact flash, and expanding ring. */
const ARROW_THUNK: VFXEffectConfig = {
  id: 'arrow_thunk',
  name: 'Arrow Thunk',
  category: 'burst',
  description: 'The arrow thunks into the target: an impact flash, embedded shaft, wood and fletching splinters, and dust ring',
  primitives: [
    starFlash({ points: 4, innerRadius: 4, outerRadius: 18, color: 0xffffff, alpha: 0.95, glow: true, expandTo: 1.5, durationMs: 140 }),
    { type: 'rect', width: 22, height: 3, color: WOOD, alpha: 0.95, depth: 98, offsetX: -8,
      tween: { alpha: 0, duration: 280, ease: 'Power1', delay: 80 } },
    shardBurst({ count: 6, spread: 22, color: WOOD, shard: { width: 7, height: 2 }, durationMs: 280 }),
    shardBurst({ count: 4, spread: 18, color: FLETCH, shard: { width: 5, height: 2 }, durationMs: 240, delayMs: 20 }),
    ringBlast({ radius: 10, color: WOOD_DARK, alpha: 0.65, stroke: { color: 0xffe0b0, width: 1.5 }, expandTo: 2.5, durationMs: 260 }),
  ],
};

/** Arrow Shot's impact: heavy arrow strike with sharp splinter burst and kinetic ring */
const ARROW_HIT: VFXEffectConfig = {
  id: 'arrow_hit',
  name: 'Arrow Hit',
  category: 'burst',
  description: 'Arrow Shot strikes home: sharp impact burst, flying wood splinters and kinetic shock ring',
  primitives: [
    starFlash({ points: 4, innerRadius: 5, outerRadius: 20, color: 0xffeedd, alpha: 1, glow: true, expandTo: 1.8, durationMs: 160 }),
    { type: 'rect', width: 30, height: 3.5, color: BROWN, alpha: 0.95, depth: 98, offsetX: -10, glow: true,
      tween: { alpha: 0, duration: 320, ease: 'Power1', delay: 100 } },
    shardBurst({ count: 8, spread: 28, color: WOOD, shard: { width: 8, height: 2.5 }, durationMs: 320 }),
    shardBurst({ count: 5, spread: 22, color: 0xffddaa, shard: { width: 6, height: 2 }, durationMs: 260, delayMs: 30 }),
    ringBlast({ radius: 14, color: WOOD_DARK, alpha: 0.7, stroke: { color: 0xffffff, width: 2 }, expandTo: 2.8, durationMs: 300 }),
  ],
};

/** Aimed Shot's wind-up: a red reticle closes on the hunter as the bow is drawn. */
const AIMED_SHOT_DRAW: VFXEffectConfig = {
  id: 'aimed_shot_draw',
  name: 'Aimed Shot Draw',
  category: 'burst',
  description: 'A red reticle closes in on the hunter over the half-second draw, four tick marks tightening with it',
  primitives: [
    // A stroked ring (an arc, so it reads from the first frame) shrinking onto the hunter.
    { type: 'arc', arcRadius: 46, arcStartAngle: 0, arcEndAngle: 360, width: 3, color: RED, alpha: 0.9, depth: 101,
      tween: { scale: 0.45, duration: 480, ease: 'Sine.easeIn' } },
    { type: 'arc', arcRadius: 52, arcStartAngle: 0, arcEndAngle: 360, width: 6, color: RED, alpha: 0.25, depth: 100,
      tween: { scale: 0.45, alpha: 0.6, duration: 480, ease: 'Sine.easeIn' } },
    { type: 'rect', width: 12, height: 3, color: RED_HOT, alpha: 0.9, depth: 102,
      scatter: { count: 4, radius: 40 },
      tween: { scale: 0.5, duration: 480, ease: 'Sine.easeIn' } },
    { type: 'circle', radius: 3, color: RED_HOT, alpha: 0.3, depth: 102, glow: true,
      tween: { scale: 2.2, alpha: 1, duration: 480, ease: 'Sine.easeIn' } },
  ],
};

/** Aimed Shot's release at the bow: a red snap. */
const AIMED_SHOT_RELEASE: VFXEffectConfig = {
  id: 'aimed_shot_release',
  name: 'Aimed Shot Release',
  category: 'burst',
  description: 'A red snap at the bow as the aimed arrow leaves',
  primitives: [
    ringBlast({ radius: 7, color: RED, alpha: 0.75, glow: true, expandTo: 2.4, durationMs: 180 }),
    glintScatter({ count: 3, spread: 10, color: RED_HOT, durationMs: 160 }),
  ],
};

/** Aimed Shot's hit: the arrow punches clean through — a red streak and a ring. */
const AIMED_SHOT_PIERCE: VFXEffectConfig = {
  id: 'aimed_shot_pierce',
  name: 'Aimed Shot Pierce',
  category: 'burst',
  description: 'The aimed arrow punches through: a bright red streak out the far side, a ring and thrown shards',
  primitives: [
    { type: 'rect', width: 46, height: 3, color: RED_HOT, alpha: 0.95, depth: 98, glow: true,
      tween: { x: 26, scaleX: 0.3, alpha: 0, duration: 200, ease: 'Power2' } },
    ringBlast({ radius: 12, color: RED, alpha: 0.65, stroke: { color: RED_HOT, width: 2 }, expandTo: 2.4, durationMs: 300 }),
    shardBurst({ count: 5, spread: 20, color: RED, shard: { width: 7, height: 2 }, durationMs: 300, delayMs: 30 }),
  ],
};

/** A Multiple Arrows arrow landing: a green-fletched nick. */
const ARROW_VOLLEY_NICK: VFXEffectConfig = {
  id: 'arrow_volley_nick',
  name: 'Arrow Volley Nick',
  category: 'burst',
  description: 'One arrow of the volley lands: a green fletching flick and two small splinters',
  primitives: [
    { type: 'polygon', polygonPoints: [-8, -5, 0, 0, -8, 5, -5, 0], color: FLETCH, alpha: 0.95, depth: 97,
      tween: { x: -10, alpha: 0, duration: 260, ease: 'Power2' } },
    shardBurst({ count: 2, spread: 10, color: WOOD, shard: { width: 5, height: 1.5 }, durationMs: 220 }),
    ringBlast({ radius: 5, color: FLETCH, alpha: 0.5, expandTo: 2, durationMs: 200 }),
  ],
};

/** Disengage's cast: the hunter kicks off backwards — dust and wind streaks. */
const DISENGAGE_KICK: VFXEffectConfig = {
  id: 'disengage_kick',
  name: 'Disengage Kick',
  category: 'burst',
  description: 'The hunter kicks off backwards: a dust cloud at the feet and three slate wind streaks thrown forward',
  primitives: [
    groundCloud({ width: 56, height: 16, color: 0xbbaa88, alpha: 0.5, offsetY: 18, swellTo: 1.6, durationMs: 420 }),
    { type: 'rect', width: 26, height: 3, color: SLATE, alpha: 0.85, depth: 96, offsetX: 16, offsetY: -10,
      tween: { x: 22, alpha: 0, duration: 240, ease: 'Power2' } },
    { type: 'rect', width: 32, height: 3, color: SLATE, alpha: 0.85, depth: 96, offsetX: 18,
      tween: { x: 26, alpha: 0, duration: 260, ease: 'Power2' } },
    { type: 'rect', width: 26, height: 3, color: SLATE, alpha: 0.85, depth: 96, offsetX: 16, offsetY: 10,
      tween: { x: 22, alpha: 0, duration: 240, ease: 'Power2' } },
    ringBlast({ radius: 12, color: SLATE, alpha: 0.4, stroke: { color: 0xd6e4f2, width: 2 }, expandTo: 2.2, durationMs: 300 }),
  ],
};

/** Disengage's landing beat: a soft dust puff at the feet when the hunter lands backwards. */
const DISENGAGE_LANDING: VFXEffectConfig = {
  id: 'disengage_landing',
  name: 'Disengage Landing',
  category: 'burst',
  description: 'Soft dust puff and small ring at the feet where the hunter lands',
  primitives: [
    groundCloud({ width: 44, height: 12, color: 0xbbaa88, alpha: 0.45, offsetY: 18, swellTo: 1.4, durationMs: 320 }),
    ringBlast({ radius: 8, color: SLATE, alpha: 0.35, stroke: { color: 0xd6e4f2, width: 1.5 }, expandTo: 1.8, durationMs: 250 }),
  ],
};

/** Rain of Arrows' tell: the volley is loosed into the sky over the wind-up. */
const RAIN_OF_ARROWS_VOLLEY: VFXEffectConfig = {
  id: 'rain_of_arrows_volley',
  name: 'Rain of Arrows Volley',
  category: 'burst',
  description: 'Brown arrows streak up off the hunter into the sky during the wind-up; a brown ring marks the release',
  primitives: [
    { type: 'rect', width: 2, height: 18, color: WOOD, alpha: 0.95, depth: 101,
      scatter: { count: 5, radius: 10 },
      tween: { y: -120, alpha: 0, duration: 520, ease: 'Quad.easeIn' } },
    { type: 'rect', width: 2, height: 14, color: BROWN, alpha: 0.9, depth: 101,
      scatter: { count: 4, radius: 16 },
      tween: { y: -110, alpha: 0, duration: 600, ease: 'Quad.easeIn', delay: 120 } },
    ringBlast({ radius: 14, color: BROWN, alpha: 0.4, stroke: { color: WOOD, width: 2 }, expandTo: 2, durationMs: 360 }),
  ],
};

/** A Rain of Arrows volley landing on a target: arrows drive down and dust kicks up. */
const ARROW_RAIN_STRIKE: VFXEffectConfig = {
  id: 'arrow_rain_strike',
  name: 'Arrow Rain Strike',
  category: 'burst',
  description: 'Three brown arrows drive down into the target and a puff of dust kicks up',
  primitives: [
    { type: 'polygon', polygonPoints: [-1, -16, 1, -16, 1, 0, 4, 0, 0, 7, -4, 0, -1, 0], color: BROWN, alpha: 0.95, depth: 97,
      scatter: { count: 3, radius: 9 }, offsetY: -12,
      tween: { y: 12, alpha: 0, duration: 240, ease: 'Power3' } },
    groundCloud({ width: 30, height: 10, color: 0xbbaa88, alpha: 0.45, offsetY: 10, durationMs: 340 }),
  ],
};

/** Concussive Shot's release: an amber thump at the bow. */
const CONCUSSIVE_SHOT_RELEASE: VFXEffectConfig = {
  id: 'concussive_shot_release',
  name: 'Concussive Shot Release',
  category: 'burst',
  description: 'A heavy amber thump at the bow — the blunt arrow is loosed',
  primitives: [
    ringBlast({ radius: 9, color: AMBER, alpha: 0.7, stroke: { color: AMBER_HOT, width: 2 }, expandTo: 2, durationMs: 200 }),
  ],
};

/** Concussive Shot's hit: an amber shock ring and dazed stars over the target. */
const CONCUSSION: VFXEffectConfig = {
  id: 'concussion',
  name: 'Concussion',
  category: 'burst',
  description: 'An amber shock ring on the target and three dazed stars wheeling over its head',
  primitives: [
    ringBlast({ radius: 14, color: AMBER, alpha: 0.6, stroke: { color: AMBER_HOT, width: 3 }, expandTo: 2.4, durationMs: 340 }),
    { type: 'star', points: 4, innerRadius: 2, outerRadius: 6, color: AMBER_HOT, alpha: 1, depth: 98, offsetY: -26,
      scatter: { count: 3, radius: 14 },
      tween: { rotation: Math.PI * 2, alpha: 0, duration: 700, ease: 'Sine.easeOut' } },
  ],
};

/** Explosive Shot's release: a lit fuse sparks at the bow. */
const EXPLOSIVE_SHOT_SPARK: VFXEffectConfig = {
  id: 'explosive_shot_spark',
  name: 'Explosive Shot Spark',
  category: 'burst',
  description: 'The charge on the arrow catches: orange fuse sparks spit off the bow',
  primitives: [
    glintScatter({ count: 5, spread: 12, color: FLAME_HOT, durationMs: 220 }),
    ringBlast({ radius: 6, color: FLAME, alpha: 0.7, glow: true, expandTo: 2, durationMs: 180 }),
  ],
};

/** Explosive Shot's splash: drawn at the 80 px splash radius (areaRadius fits it exactly). */
const EXPLOSIVE_SHOT_BLAST: VFXEffectConfig = {
  id: 'explosive_shot_blast',
  name: 'Explosive Shot Blast',
  category: 'burst',
  description: 'The arrow detonates: a white-hot core, a flame-orange fireball to the splash edge and flying embers',
  primitives: [
    ...stagedBlast(
      { radius: 18, color: FLAME_HOT, alpha: 0.95, glow: true, expandTo: 1.8, durationMs: 200 },
      { radius: 40, color: FLAME, alpha: 0.55, stroke: { color: FLAME_HOT, width: 3 }, expandTo: 2, durationMs: 340 },
      40,
    ),
    wispRise({ count: 6, spread: 44, radius: 3, color: FLAME_HOT, travel: 28, durationMs: 460, glow: true }),
  ],
};

/** Explosive Shot's direct hit: a flame lick on the target the arrow struck. */
const EXPLOSIVE_SHOT_SEAR: VFXEffectConfig = {
  id: 'explosive_shot_sear',
  name: 'Explosive Shot Sear',
  category: 'burst',
  description: 'Flame licks up the target the explosive arrow struck',
  primitives: [
    wispRise({ count: 4, spread: 10, radius: 4, color: FLAME, travel: 26, durationMs: 380 }),
    ringBlast({ radius: 8, color: FLAME, alpha: 0.6, expandTo: 2, durationMs: 240 }),
  ],
};

/** Kill Shot's release: a gold crosshair flash at the bow. */
const KILL_SHOT_FLASH: VFXEffectConfig = {
  id: 'kill_shot_flash',
  name: 'Kill Shot Flash',
  category: 'burst',
  description: 'A gold crosshair flashes at the bow as the killing arrow leaves',
  primitives: [
    { type: 'rect', width: 22, height: 2, color: GOLD, alpha: 1, depth: 101, glow: true,
      tween: { scale: 1.6, alpha: 0, duration: 200, ease: 'Power2' } },
    { type: 'rect', width: 2, height: 22, color: GOLD, alpha: 1, depth: 101, glow: true,
      tween: { scale: 1.6, alpha: 0, duration: 200, ease: 'Power2' } },
    ringBlast({ radius: 8, color: GOLD, alpha: 0.6, expandTo: 2.2, durationMs: 220 }),
  ],
};

/** Kill Shot's hit: a gold-white crosshair burst — the finisher lands. */
const KILL_SHOT_IMPACT: VFXEffectConfig = {
  id: 'kill_shot_impact',
  name: 'Kill Shot Impact',
  category: 'burst',
  description: 'A gold crosshair snaps onto the target, a white-hot core bursts and gold shards fly — the finishing shot',
  primitives: [
    { type: 'rect', width: 60, height: 3, color: GOLD, alpha: 0.95, depth: 98, glow: true,
      tween: { scaleX: 0.2, alpha: 0, duration: 260, ease: 'Power2' } },
    { type: 'rect', width: 3, height: 60, color: GOLD, alpha: 0.95, depth: 98, glow: true,
      tween: { scaleY: 0.2, alpha: 0, duration: 260, ease: 'Power2' } },
    starFlash({ points: 4, innerRadius: 5, outerRadius: 22, color: 0xffffff, glow: true, expandTo: 1.6, durationMs: 260, delayMs: 60 }),
    ringBlast({ radius: 16, color: GOLD, alpha: 0.6, stroke: { color: 0xffffff, width: 3 }, expandTo: 2.6, durationMs: 380, delayMs: 60 }),
    shardBurst({ count: 7, spread: 30, color: GOLD, shard: { width: 8, height: 2 }, durationMs: 360, delayMs: 60 }),
  ],
};

/** Deterrence's cast: three sky-blue deflecting blades spin up around the hunter. */
const DEFLECT_SHIELD: VFXEffectConfig = {
  id: 'deflect_shield',
  name: 'Deflect Shield',
  category: 'burst',
  description: 'Three sky-blue blades spin up around the hunter inside a bright ward ring — attacks will glance off',
  primitives: [
    ringBlast({ radius: 34, color: SKY, alpha: 0.35, stroke: { color: SKY_CORE, width: 3 }, expandTo: 1.5, durationMs: 460 }),
    { type: 'ellipse', width: 30, height: 6, color: SKY_CORE, alpha: 0.95, depth: 101,
      scatter: { count: 3, radius: 30 },
      tween: { rotation: Math.PI * 2, alpha: 0, duration: 520, ease: 'Sine.easeOut' } },
    glintScatter({ count: 4, spread: 30, color: SKY_CORE, durationMs: 260, delayMs: 120 }),
  ],
};

export const HUNTER_KIT_VFX: VFXEffectConfig[] = [
  ARROW_THUNK,
  ARROW_HIT,
  AIMED_SHOT_DRAW,
  AIMED_SHOT_RELEASE,
  AIMED_SHOT_PIERCE,
  ARROW_VOLLEY_NICK,
  DISENGAGE_KICK,
  DISENGAGE_LANDING,
  RAIN_OF_ARROWS_VOLLEY,
  ARROW_RAIN_STRIKE,
  CONCUSSIVE_SHOT_RELEASE,
  CONCUSSION,
  EXPLOSIVE_SHOT_SPARK,
  EXPLOSIVE_SHOT_BLAST,
  EXPLOSIVE_SHOT_SEAR,
  KILL_SHOT_FLASH,
  KILL_SHOT_IMPACT,
  DEFLECT_SHIELD,
];
